import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { waUrl } from "@/lib/waTemplates";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { selectCls } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { Star, MessageCircle, Check } from "lucide-react";

export function useSatisfaction(params) {
  const [d, setD] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(() => { api.get("/crm/satisfaction", { params: JSON.parse(key) }).then(({ data }) => setD(data)).catch(() => setD(null)); }, [key]);
  useEffect(() => { load(); }, [load]);
  return [d, load];
}

export default function OwnerSatisfaction() {
  const [f, setF] = useState({ from: "", to: "", mechanic: "" });
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const [d, load] = useSatisfaction(params);
  const resolve = async (it) => { try { await api.post(`/crm/satisfaction/${it.id}/resolved`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const max = d ? Math.max(1, ...Object.values(d.dist)) : 1;
  return (
    <div className="space-y-4 animate-fade-up" data-testid="owner-satisfaction">
      <div><div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase"><Star className="h-4 w-4" /> CRM</div>
        <h1 className="font-display text-3xl font-black uppercase text-zinc-100 mt-1">Satisfação</h1></div>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-[11px] text-zinc-400">De<Input data-testid="sat-from" type="date" value={f.from} onChange={(e) => setF((p) => ({ ...p, from: e.target.value }))} className="mt-1 bg-zinc-900 border-zinc-800 min-w-0" /></label>
        <label className="text-[11px] text-zinc-400">Até<Input data-testid="sat-to" type="date" value={f.to} onChange={(e) => setF((p) => ({ ...p, to: e.target.value }))} className="mt-1 bg-zinc-900 border-zinc-800 min-w-0" /></label>
        <select data-testid="sat-mechanic" value={f.mechanic} onChange={(e) => setF((p) => ({ ...p, mechanic: e.target.value }))} className={`${selectCls} w-48`}>
          <option value="">Todos os mecânicos</option>{(d?.mechanics || []).map((m) => <option key={m} value={m}>{m}</option>)}</select>
      </div>
      {d && (
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3">
          <div className="rounded-2xl border border-card2-border bg-card2 p-5 text-center" data-testid="sat-avg">
            <div className="font-display text-5xl font-black text-zinc-100">{d.avg ?? "—"}</div>
            <div className="text-amber-400 text-xl">{"★".repeat(Math.round(d.avg || 0))}</div>
            <div className="text-xs text-zinc-500">{d.count} avaliação(ões)</div>
          </div>
          <div className="rounded-2xl border border-card2-border bg-card2 p-5 space-y-1" data-testid="sat-dist">
            {[5, 4, 3, 2, 1].map((n) => (
              <div key={n} className="flex items-center gap-2 text-sm"><span className="w-8 text-zinc-400">{n}★</span>
                <div className="flex-1 h-3 rounded-full bg-zinc-800 overflow-hidden"><div className={`h-full ${n >= 4 ? "bg-green-500" : n === 3 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${(d.dist[n] / max) * 100}%` }} /></div>
                <span className="w-8 text-right text-zinc-300">{d.dist[n]}</span></div>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-card2-border bg-card2 divide-y divide-zinc-800">
        {d?.items.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">Nenhuma avaliação no período.</div>}
        {d?.items.map((it) => (
          <div key={it.id} data-testid={`sat-item-${it.id}`} className={`p-3 space-y-1 ${it.rating <= 3 && !it.resolved_at ? "bg-red-950/20" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm"><b className={it.rating <= 3 ? "text-red-400" : "text-amber-400"}>{"★".repeat(it.rating)}</b> <span className="text-zinc-200">{it.customer_name || "—"}</span> <span className="text-zinc-500 text-xs">· OS <Link to={`/owner/ordens/${it.order_id}`} className="underline">#{it.order_number || ""}</Link> · {it.plate} {it.mechanic_name ? `· ${it.mechanic_name}` : ""} · {fmtDateTime(it.answered_at)}</span></span>
              {it.rating <= 3 && !it.resolved_at && <div className="flex gap-2">
                {it.customer_phone && <a data-testid={`sat-wa-${it.id}`} href={waUrl(it.customer_phone, `Olá, ${it.customer_name || ""}! Vimos sua avaliação e queremos resolver. Podemos conversar?`)} target="_blank" rel="noopener noreferrer" className="h-8 px-2 inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>}
                <Button data-testid={`sat-resolve-${it.id}`} size="sm" variant="outline" onClick={() => resolve(it)} className="h-8 bg-zinc-900 border-zinc-800"><Check className="h-3.5 w-3.5 mr-1" /> Resolvido</Button></div>}
            </div>
            {it.comment && <div className="text-sm text-zinc-400">“{it.comment}”</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
